/* merchant/sagas*/
import {
  select,
  all,
  fork,
  put,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { has, isNil, includes, isEmpty } from "ramda";
import { Merchant, MerchantActionTypes } from "./types";
import { fetch, save, drop, fetchAll } from "../../repo/merchant";
import {
  fetchRequest,
  fetchError,
  fetchSuccess,
  fetchAllSuccess,
  saveRequest,
  saveSuccess,
  saveError,
  deleteRequest,
  deleteSuccess,
  deleteError,
} from "./actions";
import { dirty } from "../session/actions";
import { Error, isAuthError } from "../";
import { get } from "lodash";
import { exportToVSC, flat } from "../../lib/utils";
function* handleFetch(action: ReturnType<typeof fetchRequest>) {
  try {
    const merchant = yield fetch(action.payload.id, action.payload.params);
    if (merchant.errors && !isEmpty(merchant.errors)) {
      if (isAuthError(merchant.errors)) {
        yield put(dirty());
      }
      yield put(fetchError(JSON.stringify(merchant.errors)));
    } else {
      yield put(fetchSuccess(merchant));
    }
  } catch (err) {
    yield put(fetchError(err));
  }
}

function* handleFetchAll(action: ReturnType<typeof fetchRequest>) {
  try {
    const merchantall = yield fetchAll(
      action.payload.id,
      action.payload.params
    );
    if (merchantall.errors && !isEmpty(merchantall.errors)) {
      if (isAuthError(merchantall.errors)) {
        yield put(dirty());
      }
      yield put(fetchError(JSON.stringify(merchantall.errors)));
    } else {
      yield put(fetchAllSuccess(merchantall));
    }
  } catch (err) {
    yield put(fetchError(err));
  }
}

function* watchFetchRequest() {
  yield takeEvery(MerchantActionTypes.FETCH, handleFetch);
}

function* watchFetchAllRequest() {
  yield takeEvery(MerchantActionTypes.FETCH_ALL, handleFetchAll);
}

function* handleSave(action: ReturnType<typeof saveRequest>) {
  try {
    // If either phone or mobile field is left empty, the form will
    // insert "null" to the submission payload which causes issues with io-ts
    // merchant codec. Here we detect for null fields and remove it from the request body.
    if (action.payload.data.attributes.Contact) {
      action.payload.data.attributes.Contact.forEach((contact) => {
        if (!contact.Phone) {
          delete contact.Phone;
        }
        if (!contact.Mobile) {
          delete contact.Mobile;
        }
      });
    }
    const merchant = yield save(action.payload.data);
    // we may still have an error
    // if so then we should yield error
    if (merchant.errors && !isEmpty(merchant.errors)) {
      if (isAuthError(merchant.errors)) {
        yield put(dirty());
      }
      yield put(saveError(JSON.stringify(merchant.errors)));
      if (action.payload.callback) {
        // Try to extract duplicate key error for better error message
        const errorDetail = merchant.errors[0]?.detail;
        let errorMessage = typeof errorDetail === "string" ? errorDetail : JSON.stringify(errorDetail);

        // Check for MongoDB duplicate key error
        if (
          errorMessage &&
          errorMessage.includes("E11000 duplicate key error")
        ) {
          // Try to extract the key and value
          const match = errorMessage.match(/dup key: (.*)/);
          let duplicateKey = "";
          if (match && match[1]) {
            duplicateKey = match[1].trim();
          }
          errorMessage = "Duplicate key error. " + (duplicateKey ? `Duplicate entry: ${duplicateKey}` : "A record with the same key already exists.");
        }

        action.payload.callback(errorMessage, null);
      }
    } else {
      yield put(saveSuccess(merchant));
      action.payload.callback && action.payload.callback(null, merchant);
    }
  } catch (err) {
    yield put(saveError(err));
    action.payload.callback &&
      action.payload.callback("An error occurred while saving merchant", null);
  }
}

function* watchSaveRequest() {
  yield takeEvery(MerchantActionTypes.SAVE_REQUEST, handleSave);
}

function* handleDelete(action: ReturnType<typeof deleteRequest>) {
  try {
    const dropResponse = yield drop(action.payload.id);

    if (dropResponse.errors && !isEmpty(dropResponse.errors)) {
      if (isAuthError(dropResponse.errors)) {
        yield put(dirty());
      }
      yield put(deleteError(JSON.stringify(dropResponse.errors)));
      action.payload.callback &&
        action.payload.callback(JSON.stringify(dropResponse.errors));
    } else {
      yield put(deleteSuccess());
      action.payload.callback && action.payload.callback(null);
      yield put(fetchRequest(null));
    }
  } catch (err) {
    yield put(deleteError(err));
    action.payload.callback && action.payload.callback(JSON.stringify(err));
  }
}

function* handleDownloadRequest(action: ReturnType<typeof fetchRequest>) {
  try {
    const response = yield fetch(action.payload.id, action.payload.params);
    if (response.errors && !isEmpty(response.errors)) {
      if (isAuthError(response.errors)) {
        yield put(dirty());
      }
      yield put(fetchError(JSON.stringify(response.errors)));
    } else {

      // export to csv file
      const header = action.payload.params["header"] ?? [];
      exportToVSC(response.data,header);
    }
  } catch (err) {
    yield put(fetchError(err));
  }
}

function* watchDeleteRequest() {
  yield takeLatest(MerchantActionTypes.DELETE_REQUEST, handleDelete);
}

function* watchDownloadRequest() {
  // yield takeEvery(MerchantActionTypes.DOWNLOAD_REQUEST, handleFetchAll);
  yield takeEvery(MerchantActionTypes.DOWNLOAD_REQUEST, handleDownloadRequest);
}

export default function* merchantSaga() {
  yield all([
    fork(watchFetchRequest),
    fork(watchSaveRequest),
    fork(watchDeleteRequest),
    fork(watchFetchAllRequest),
    fork(watchDownloadRequest),
  ]);
}
